/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Cell, Grid, HeadingSection, VFlow } from 'bold-ui'
import CheckPermission from 'components/auth/CheckPermission'
import { Box } from 'components/Box'
import { BoxGroup } from 'components/BoxGroup'
import { resolveName, usePecField } from 'components/form/final-form/useField'
import { SexoEnum } from 'graphql/types.generated'
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays'
import Permissions from 'types/Permissions'
import { MetaPath } from 'util/metaPath'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import AtestadoModel, { CidadaoAtestado } from './components/AtestadoModel'
import { AtestadoSelectField, AtestadoSelectModel, ModeloAtestadoEnum } from './components/AtestadoSelectField'
import DeclaracaoComparecimentoBox from './DeclaracaoComparecimentoBox'
import AtestadoEmBrancoForm from './em-branco/AtestadoEmBrancoForm'
import LicencaMaternidadeForm from './licenca-maternidade/LicencaMaternidadeForm'
import AtestadosTable from './list/AtestadosTable'
import AtestadoPadraoView from './padrao/AtestadoPadraoView'

export interface AtestadoViewProps {
  name: MetaPath<AtestadoPlanoModel>
  atendimentoId: ID
  prontuarioId: ID
  cidadao: CidadaoAtendimento
  dataAtendimento: Instant
}

export interface AtestadoPlanoModel {
  modeloAtestado: AtestadoSelectModel
  atestadosRecentes?: AtestadoModel[]
}

export default function AtestadoView(props: AtestadoViewProps) {
  const { name, atendimentoId, prontuarioId, cidadao, dataAtendimento } = props
  const { input } = usePecField({ name })

  const canAccessLicencaMaternidade =
    cidadao?.idade > 5 && (cidadao?.identidadeGeneroDbEnum !== null || cidadao?.sexo === SexoEnum.FEMININO)

  const renderFieldArray = (fieldArrayRenderProps: FieldArrayRenderProps<AtestadoModel, any>) => {
    const {
      fields: { push, remove },
    } = fieldArrayRenderProps

    const handleSubmit = (values: AtestadoModel) => {
      push(values)
    }

    return (
      <HeadingSection
        level={3}
        title='Atestados'
        vSpace={8}
        style={css`
          margin-top: 1rem;
        `}
      >
        <VFlow vSpacing={2}>
          <BoxGroup>
            <CheckPermission
              permission={Permissions.visualizarProntuario.soap.plano.visualizarAtestados.cadastrarEditarEExcluir}
            >
              <Box>
                <Grid>
                  <Cell size={3}>
                    <AtestadoSelectField
                      required
                      label='Modelo'
                      name={name.modeloAtestado}
                      canAccessLicencaMaternidade={canAccessLicencaMaternidade}
                    />
                  </Cell>
                  {(!input.value.modeloAtestado ||
                    input.value.modeloAtestado?.value === ModeloAtestadoEnum.EM_BRANCO) && (
                    <Cell size={12}>
                      <AtestadoEmBrancoForm onSubmit={handleSubmit} />
                    </Cell>
                  )}
                  {input.value.modeloAtestado?.value === ModeloAtestadoEnum.LICENCA_MATERNIDADE && (
                    <LicencaMaternidadeForm
                      onSubmit={handleSubmit}
                      cidadao={convertCidadaoAtestado(cidadao)}
                      dataAtendimento={dataAtendimento}
                    />
                  )}
                  {input.value.modeloAtestado?.value === ModeloAtestadoEnum.PADRAO && (
                    <AtestadoPadraoView
                      name={name.atestadosRecentes}
                      cidadao={convertCidadaoAtestado(cidadao)}
                      dataAtendimento={dataAtendimento}
                    />
                  )}
                </Grid>
              </Box>
            </CheckPermission>
            <CheckPermission permission={Permissions.visualizarListaDeAtendimento.gerarDeclaracaoDeComparecimento}>
              <DeclaracaoComparecimentoBox atendimentoId={atendimentoId} />
            </CheckPermission>
          </BoxGroup>
          <AtestadosTable
            atendimentoId={atendimentoId}
            prontuarioId={prontuarioId}
            name={name.atestadosRecentes}
            remove={remove}
          />
        </VFlow>
      </HeadingSection>
    )
  }

  return <FieldArray name={resolveName(name.atestadosRecentes)} render={renderFieldArray} />
}

const convertCidadaoAtestado = (cidadao: CidadaoAtendimento) => {
  return {
    id: cidadao.id,
    nome: cidadao.nome,
    nomeSocial: cidadao.nomeSocial,
    cpf: cidadao.cpf,
    cns: cidadao.cns,
  } as CidadaoAtestado
}
